import { ModelStatus } from '@df/catalog/model';
import { app } from '@df/core/app';
import { NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { ICmsContentService } from '../icms-content.service';
import { NICmsContentField } from '../interfaces/icms-content-field.interfaces';
import { NICmsContentPageType } from '../interfaces/icms-content-page-type.interfaces';
import { IPageDataV4 } from '@df/catalog/cms/cms-model.interface';

export const iCmsPageSeoTab = 'SEO & Opengraph';

export const DEFAULT_PAGE_FIELDS: NICmsContentPageType.IField[] = [
    {
        key: 'name',
        title: 'Page name',
        description: 'This name is used in the CMS to identify the page. If meta title is not set the page name will be used instead',
        placeholder: 'Page name e.g. new-page',
        required: true,
        type: NICmsContentField.EFieldType.text,
        initial: true,
        mirrorEditable: true
    },
    <NICmsContentField.IChoiceField<string>>{
        key: 'pageType',
        title: 'Page type',
        required: true,
        type: NICmsContentField.EFieldType.choice,
        options: () => Object.values(ICmsContentService.pageTypes).map(i => ({ key: i.id, title: i.title })),
        default: NICmsContentPageType.defaultPageTypeId,
        initial: true
    },

    <NICmsContentField.IChoiceField<ModelStatus>>{
        key: '_cms.status',
        title: 'Status',
        required: true,
        type: NICmsContentField.EFieldType.choice,
        options: [
            { key: ModelStatus.enabled, title: 'Enabled' },
            { key: ModelStatus.disabled, title: 'Disabled' },
            { key: ModelStatus.deleted, title: 'Deleted' }
        ],
        mirrorEditable: true
    },
    {
        key: 'image',
        title: 'Thumbnail',
        type: NICmsContentField.EFieldType.image,
        mirrorEditable: true
    },
    <NICmsContentField.IRadioField<boolean>>{
        key: '_cms.absoluteUrl',
        title: 'URL',
        required: true,
        type: NICmsContentField.EFieldType.radio,
        options: [
            { key: true, title: 'Absolute' },
            { key: false, title: 'Relative' }
        ],
        initial: true,
        mirrorEditable: true,
        onChange: event => {
            const data = event.scopeData as IPageDataV4;
            const absoluteUrl = event.value;
            if (data._cms) {
                const currentUrl = data._cms.url || data._cms.slug;
                return {
                    changes: [
                        {
                            changeType: NICmsDataChange.EChangeType.change,
                            value: absoluteUrl ? '' : currentUrl,
                            scopeData: data._cms,
                            key: 'slug'
                        },
                        {
                            changeType: NICmsDataChange.EChangeType.change,
                            value: absoluteUrl ? currentUrl : '',
                            scopeData: data._cms,
                            key: 'url'
                        }
                    ]
                };
            }
            return;
        }
    },
    {
        key: '_cms.url',
        title: 'URL',
        required: true,
        type: NICmsContentField.EFieldType.text,
        canShow: (data: IPageDataV4) => data?._cms?.absoluteUrl,
        initial: true,
        display: 'mui',
        mirrorEditable: true,
        onFocus: event => {
            const data = event.scopeData as IPageDataV4;
            if (!event.value) {
                const value = name2url(data.name);
                if (value) {
                    return {
                        changes: [
                            {
                                changeType: NICmsDataChange.EChangeType.change,
                                value,
                                scopeData: data._cms,
                                key: 'url'
                            }
                        ]
                    };
                }
            }
            return;
        }
    },
    {
        key: '_cms.slug',
        title: 'URL key',
        required: true,
        type: NICmsContentField.EFieldType.text,
        canShow: (data: IPageDataV4) => !data?._cms?.absoluteUrl,
        initial: true,
        display: 'mui',
        mirrorEditable: true,

        onFocus: event => {
            const data = event.scopeData as IPageDataV4;
            if (!event.value) {
                const value = name2url(data.name);
                if (value) {
                    return {
                        changes: [
                            {
                                changeType: NICmsDataChange.EChangeType.change,
                                value,
                                scopeData: data._cms,
                                key: 'slug'
                            }
                        ]
                    };
                }
            }
            return;
        }
    },
    <NICmsContentField.IRelationField<IPageDataV4, ICmsPage>>{
        key: 'parent',
        title: 'Parent page',
        required: (data: IPageDataV4) => !data?._cms?.absoluteUrl,
        type: NICmsContentField.EFieldType.relation,
        initial: true,
        options: {
            title: 'Select parent page',
            type: 'page',
            // skip self and homepage
            filter: (model: ICmsPage, data: IPageDataV4) => model.id !== data.id && model.url !== '/'
        },
        mirrorEditable: true
    },

    <NICmsContentField.IChoiceField<number>>{
        key: 'groups',
        title: 'Groups',
        type: NICmsContentField.EFieldType.choice,
        multiple: true,
        prepare: () => app.resources.group.loadAll(),
        options: () => app.resources.group.getAllExisting().map(i => ({ key: i.id, title: i.name })),
        mirrorEditable: true
    },

    {
        key: 'sectionSeoLabel',
        title: 'SEO information',
        type: NICmsContentField.EFieldType.label,
        tab: iCmsPageSeoTab,
        mirrorEditable: true
    },

    {
        key: 'meta.title',
        title: 'Title tag',
        type: NICmsContentField.EFieldType.text,
        tab: iCmsPageSeoTab,
        description: 'Optimal title length approximately 55 characters',
        mirrorEditable: true
    },

    {
        key: 'meta.description',
        title: 'Meta description',
        type: NICmsContentField.EFieldType.textarea,
        tab: iCmsPageSeoTab,
        description: 'Optimal description length approximately 155-300 characters',
        mirrorEditable: true
    },

    <NICmsContentField.IChoiceField<string>>{
        key: 'meta.robots',
        title: 'Robots',
        type: NICmsContentField.EFieldType.choice,
        options: () => ['INDEX,FOLLOW', 'NOINDEX,FOLLOW', 'INDEX,NOFOLLOW', 'NOINDEX,NOFOLLOW'].map(title => ({ key: title, title })),
        tab: iCmsPageSeoTab,
        description: 'INDEX,FOLLOW will be used if no value is picked',
        mirrorEditable: true
    },

    {
        key: 'meta.canonical',
        title: 'Custom canonical',
        type: NICmsContentField.EFieldType.text,
        tab: iCmsPageSeoTab,
        mirrorEditable: true
    },
    <NICmsContentField.IToggleField>{
        key: 'meta.nosearch',
        title: 'Not visible in search results',
        type: NICmsContentField.EFieldType.toggle,
        description: 'When this is marked page will not appear in the internal website search results',
        labels: { true: 'Not visible', false: 'Visible' },
        mirrorEditable: true
    }
];

export function name2url(name?: string): string | undefined {
    return name?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
}
